import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { apiCompensationChangeReasons } from "@/api/compensation-change-reason.api.ts";
import { ApiCompensationChangeReasonPayload } from "@/types/compensation-change-reason.types.ts";

const compensationChangeReasonsKeys = createQueryKeys("compensationChangeReasons", {
  all: null,
});

export const useCompensationChangeReasons = (enabled: boolean = true) => {
  return useQuery({
    ...compensationChangeReasonsKeys.all,
    queryFn: async () => {
      const result = await apiCompensationChangeReasons.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export interface SaveParams {
  id?: number;
  title: string;
}

export const useCompensationChangeReasonSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, title }: SaveParams) => {
      const payload: ApiCompensationChangeReasonPayload = {
        title: title,
      };
      const result = id
        ? await apiCompensationChangeReasons.update(id, payload)
        : await apiCompensationChangeReasons.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: compensationChangeReasonsKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCompensationChangeReasonDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCompensationChangeReasons.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: compensationChangeReasonsKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
